// Wire → domain. The single place snake_case turns into camelCase for this feature.
//
// The backend serializer emits `""` for absent strings (see the handoff fixture) and `null` for
// absent ids/timestamps; both normalise to `null` here so components branch on one falsy shape.
import type {
  ApprovalDecision,
  ApprovalInboxPage,
  ApprovalInboxRow,
  ApprovalState,
  PlatformActionActor,
  PlatformActionApproval,
  PlatformActionAuditEvent,
  PlatformActionDetail,
  PlatformActionFailure,
  PlatformActionPage,
  PlatformActionRequest,
  RequiredApproval,
} from '../models/platformAction.types'
import type {
  ActionDetailDto,
  ActionFailureDto,
  ActionListBodyDto,
  ActionRequestDto,
  ActorRefDto,
  ApprovalDecisionDto,
  ApprovalDto,
  ApprovalInboxRowDto,
  ApprovalListBodyDto,
  AuditEventDto,
  RequiredApprovalDto,
} from '../models/platformAction.wire'

import { readLedger, readValues } from './changeLedger'
import { adaptPreview } from './previewDiff'

/** `""` and `null` both mean "absent" on this wire. */
function text(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function record(value: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return value && typeof value === 'object' ? value : {}
}

function id(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function adaptActor(dto: ActorRefDto | null | undefined): PlatformActionActor | null {
  if (!dto || typeof dto.id !== 'number') return null
  return { id: dto.id, displayName: text(dto.display_name) ?? '' }
}

export function adaptFailure(
  dto: ActionFailureDto | null | undefined,
): PlatformActionFailure | null {
  if (!dto || !text(dto.error_class)) return null
  const meaning = dto.meaning
  return {
    errorClass: dto.error_class,
    providerCode: text(dto.provider_code),
    message: text(dto.message),
    meaning:
      meaning && text(meaning.reason)
        ? { reason: meaning.reason, fix: meaning.fix ?? '', retryable: meaning.retryable === true }
        : null,
  }
}

export function adaptActionRequest(dto: ActionRequestDto): PlatformActionRequest {
  const verifiedAt = text(dto.verified_at)
  return {
    id: dto.id,
    platform: dto.platform,
    actionType: dto.action_type,
    campaignId: dto.campaign_id,
    targetLevel: text(dto.target_level),
    targetId: text(dto.target_id),
    marketingChannel: text(dto.marketing_channel),
    payload: record(dto.payload),
    preview: adaptPreview(dto.preview),
    status: dto.status,
    surface: text(dto.surface),
    requestedById: id(dto.requested_by_id),
    asyncJobRunId: id(dto.async_job_run_id),
    externalMutationRef: text(dto.external_mutation_ref),
    errorClass: text(dto.error_class),
    errorMessage: text(dto.error_message),
    expiresAt: text(dto.expires_at),
    executedAt: text(dto.executed_at),
    verifiedAt,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    requestedBy: adaptActor(dto.requested_by),
    cancellable: dto.cancellable === true,
    providerCode: text(dto.provider_code),
    failure: adaptFailure(dto.failure),
    values: readValues(dto.preview, verifiedAt),
    ledger: readLedger(dto.preview),
  }
}

export function adaptApproval(dto: ApprovalDto): PlatformActionApproval {
  return {
    id: dto.id,
    status: dto.status,
    decidedById: id(dto.decided_by_id),
    decidedAt: text(dto.decided_at),
    comment: text(dto.comment) ?? '',
    selfApproved: dto.self_approved === true,
    expiresAt: text(dto.expires_at),
    createdAt: dto.created_at,
  }
}

export function adaptAuditEvent(dto: AuditEventDto): PlatformActionAuditEvent {
  return {
    id: dto.id,
    eventType: dto.event_type,
    actorId: id(dto.actor_id),
    surface: text(dto.surface),
    payload: record(dto.payload),
    createdAt: dto.created_at,
  }
}

const APPROVAL_STATES = new Set<string>(['awaiting_decision', 'awaiting_confirm', 'closed'])

function approvalState(value: string | null | undefined): ApprovalState | null {
  return typeof value === 'string' && APPROVAL_STATES.has(value) ? (value as ApprovalState) : null
}

export function adaptRequiredApproval(
  dto: RequiredApprovalDto | null | undefined,
): RequiredApproval | null {
  if (!dto) return null
  return {
    approvalRequired: dto.approval_required !== false,
    requiredRole: text(dto.required_role) ?? '',
    requiresSecondPerson: dto.requires_second_person === true,
  }
}

export function adaptDetail(dto: ActionDetailDto): PlatformActionDetail {
  const request = adaptActionRequest(dto.request)
  return {
    request,
    approvals: (dto.approvals ?? []).map(adaptApproval),
    audit: (dto.audit ?? []).map(adaptAuditEvent),
    // The detail route sends `failure` beside the request; older builds only on the request.
    failure: adaptFailure(dto.failure) ?? request.failure,
    approvalState: approvalState(dto.approval_state),
    proposer: adaptActor(dto.proposer),
    requiredApproval: adaptRequiredApproval(dto.required_approval),
  }
}

export function adaptApprovalDecision(dto: ApprovalDecisionDto): ApprovalDecision {
  return {
    id: dto.id,
    status: dto.status,
    decidedBy: adaptActor(dto.decided_by),
    surface: text(dto.surface),
    decidedAt: text(dto.decided_at),
    selfApproved: dto.self_approved === true,
    comment: text(dto.comment) ?? '',
    createdAt: dto.created_at,
  }
}

/** A row with no policy record reads as "nobody else signs it off", the server's own default. */
const NO_APPROVAL_STEP: RequiredApproval = {
  approvalRequired: false,
  requiredRole: '',
  requiresSecondPerson: false,
}

export function adaptInboxRow(dto: ApprovalInboxRowDto): ApprovalInboxRow {
  const ttl = dto.ttl_seconds_remaining
  return {
    request: adaptActionRequest(dto.request),
    approvalState: approvalState(dto.approval_state),
    proposer: adaptActor(dto.proposer),
    requiredApproval: adaptRequiredApproval(dto.required_approval) ?? NO_APPROVAL_STEP,
    ttlSecondsRemaining: typeof ttl === 'number' && Number.isFinite(ttl) ? ttl : null,
    grantedApproval: dto.granted_approval ? adaptApprovalDecision(dto.granted_approval) : null,
  }
}

export function adaptInboxPage(body: ApprovalListBodyDto): ApprovalInboxPage {
  const pagination = body.pagination
  return {
    rows: (body.data ?? []).map(adaptInboxRow),
    page: pagination?.page ?? 1,
    pageSize: pagination?.page_size ?? 0,
    total: pagination?.total ?? 0,
    scanTruncated: pagination?.scan_truncated === true,
  }
}

export function adaptPage(body: ActionListBodyDto): PlatformActionPage {
  const pagination = body.pagination
  return {
    requests: (body.data ?? []).map(adaptActionRequest),
    page: pagination?.page ?? 1,
    pageSize: pagination?.page_size ?? 0,
    total: pagination?.total ?? 0,
  }
}
